import $ from 'jquery';

import { logger } from '@luna/logger';
import _ from '@luna/lodash';
import { uuid } from '@luna/utils';
import { getFeatureConfig } from '@luna/feature-config';
import { getToken, logout } from '@shared/auth';
import {
    AjaxErrorDetailFactory,
    AjaxErrorTrackService,
    getBeanCounterTracker,
} from '@shared/statistics';
import {
    addAlexProxyUrl,
    isAlexProxyActive,
    isFatEnvironment,
    isProdEnvironment,
} from '@shared/environment';
import { getCurrentLanguage } from '@luna/i18n';
import { getEnv } from '@luna/env';
import type { RetailEnvironmentConfig } from '@retail/core-config';

import type { AjaxSettings, JqXHR } from './types';
import { updateProxyUrlBasedOnEnvironment } from '../startup/reverseProxy';
import { AjaxTimeTracker } from './AjaxTimeTracker';

export const X_APPLICATION_TYPE = 'george-web';
export const X_GEORGE_SCOPE_KEY = 'X-GEORGE-SCOPE';
export const ACCEPT_LANGUAGE_HEADER = 'Accept-Language';
export const OCP_APIM_SUBSCRIPTION_KEY = 'Ocp-Apim-Subscription-Key';

export const DEFAULT_ACCEPTED_LANGUAGE = '*';

const API_ERROR_TYPE = 'ERROR';
const API_ERROR_NAME = 'ERROR_API';

function isUrlPrestoOrGAPI(url: string): boolean {
    const { prestoUrl, georgeApiUrl } = getEnv<RetailEnvironmentConfig>();

    return url.indexOf(prestoUrl) > -1 || url.indexOf(georgeApiUrl) > -1;
}

const setUpAjaxOptions = (): void => {
    $.ajaxSetup({
        xhrFields: {
            withCredentials: true,
        },
        crossDomain: true,
    });
};

const setUpAjaxTracking = (): void => {
    const ajaxTimeTracker = new AjaxTimeTracker(
        $(document),
        new AjaxErrorTrackService(getBeanCounterTracker()),
    );

    ajaxTimeTracker.start();
};

const setUpAjaxErrorHandler = (): void => {
    $(document).ajaxError((ev, xhr: JqXHR, ajaxSettings: AjaxSettings) => {
        if (!xhr.handled) {
            logger.error(
                `AjaxError :: ${xhr.status} : ${xhr.statusText} | `,
                `${ajaxSettings.type} ${ajaxSettings.url} | ${xhr.responseText}`,
            );
        }

        const isUnauthorizedStatusCode =
            xhr.status === +getFeatureConfig((config) => config.base.unauthorizedStatusCode);

        if (xhr.handled === true) {
            return;
        }

        if (isUnauthorizedStatusCode && isUrlPrestoOrGAPI(ajaxSettings.url!)) {
            logout('http401');
            return;
        }

        logger.error('AjaxError :: API Error : ', xhr.status, ajaxSettings.url);

        const errorTracker = new AjaxErrorTrackService(getBeanCounterTracker());

        errorTracker.track(
            API_ERROR_TYPE,
            API_ERROR_NAME,
            AjaxErrorDetailFactory.create(ajaxSettings, xhr),
        );
    });
};

const adjustRequestHeaders = (options: AjaxSettings, jqXHR: JqXHR): void => {
    if (!options.skipAuthHeader) {
        jqXHR.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    }

    const georgeScopeHeader = getFeatureConfig((f) => f.base.headers.georgeScopeHeader);
    if (georgeScopeHeader && !options.headers?.[X_GEORGE_SCOPE_KEY]) {
        jqXHR.setRequestHeader(X_GEORGE_SCOPE_KEY, georgeScopeHeader);
    }

    if (getFeatureConfig((f) => f.base.sendLanguageHeader)) {
        jqXHR.setRequestHeader(
            ACCEPT_LANGUAGE_HEADER,
            _.escape(getCurrentLanguage() ?? DEFAULT_ACCEPTED_LANGUAGE),
        );
    }

    if (getFeatureConfig((f) => f.base.sendRequestId) && isUrlPrestoOrGAPI(options.url!)) {
        jqXHR.setRequestHeader('X-REQUEST-ID', uuid());
        jqXHR.requestId = uuid();
    }

    if (
        getEnv<RetailEnvironmentConfig>().webAPIClientId &&
        (options.url!.indexOf('/webapi') > -1 ||
            options.url!.indexOf('/geapi') > -1 ||
            options.url!.indexOf('/gebof') > -1)
    ) {
        jqXHR.setRequestHeader('WEB-API-key', getEnv<RetailEnvironmentConfig>().webAPIClientId);
    }
};

export const ajaxPrefilterCallBack = (
    options: AjaxSettings,
    originalOptions: AjaxSettings,
    jqXHR: JqXHR,
): void => {
    // CVE-2015-9251 fix, https://www.npmjs.com/advisories/328
    if (options.crossDomain && !!options.contents) {
        options.contents.script = false;
    }

    adjustRequestHeaders(options, jqXHR);

    if (
        options.url!.indexOf(getEnv<RetailEnvironmentConfig>().prestoUrl) > -1 &&
        !isProdEnvironment() &&
        !isFatEnvironment() &&
        getFeatureConfig((config) => config.base.enableTraceWebapi)
    ) {
        if (options.url!.indexOf('?') === -1) {
            options.url += '?enableTrace=true';
        } else {
            options.url += '&enableTrace=true';
        }
    }

    if (!isAlexProxyActive()) {
        options.url = updateProxyUrlBasedOnEnvironment(options.url!);
    } else {
        options.url = addAlexProxyUrl(options.url!);
    }
};

const setUpAjaxPrefilter = (): void => {
    $.ajaxPrefilter(ajaxPrefilterCallBack);
};

export const initialAJAXSetup = (): void => {
    setUpAjaxOptions();

    setUpAjaxErrorHandler();

    setUpAjaxPrefilter();

    setUpAjaxTracking();
};
